import { useCallback, useEffect, useRef } from "react";
import {
  playRegisteredGuidance,
  registerActiveGuidance,
  stopRegisteredGuidance,
  unregisterActiveGuidance,
  type AudioKey,
} from "./audio";

/**
 * Registers one mounted step as spoken guidance and exposes local controls.
 *
 * @param key - Active step key, or undefined when the step has no guidance.
 * @param autoPlay - Whether the localized clip should start on registration.
 * @returns Local controls scoped to this exact mounted-step registration.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * function CardStep() {
 *   const { play } = useAudio("checkIn-cardCheck");
 *   return <button onClick={play}>Repeat</button>;
 * }
 * ```
 */
export function useAudio(key?: AudioKey, autoPlay = true) {
  const registrationRef = useRef<number | null>(null);

  const play = useCallback(() => {
    if (registrationRef.current !== null) {
      playRegisteredGuidance(registrationRef.current);
    }
  }, []);

  const stop = useCallback(() => {
    if (registrationRef.current !== null) {
      stopRegisteredGuidance(registrationRef.current);
    }
  }, []);

  useEffect(
    function registerMountedGuidance() {
      if (key === undefined) return;

      const registration = registerActiveGuidance(key, autoPlay);
      registrationRef.current = registration;
      return function unregisterMountedGuidance() {
        unregisterActiveGuidance(registration);
        if (registrationRef.current === registration) {
          registrationRef.current = null;
        }
      };
    },
    [autoPlay, key],
  );

  return { play, stop };
}
